import Footer from "@/components/shared/Footer";
import { LanguageFlag } from "@/components/shared/LanguageFlag";
import NavbarClient from "@/components/shared/NavbarClient";
import Container from "@/components/ui/container";
import useLanguage from "@/hooks/useLanguage";
import {
	documentLanguageSectionTitleKey,
	groupDocumentsByLanguage,
	type DocumentLanguageSection,
} from "@/lib/groupDocuments";
import AppService from "@/services/AppService";
import type { ClientData } from "@/types/client/clientData";
import type { Document } from "@/types/document/document";
import type { Link } from "@/types/link/link";
import { useQuery } from "@tanstack/react-query";
import { ArrowUpRight, FileIcon, Globe, LinkIcon, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { toast } from "sonner";

const DocumentsPage = () => {
	const { id } = useParams<{ id: string }>();
	const { t } = useTranslation();
	const { currentLanguage } = useLanguage();
	const appService = new AppService();

	const { data, isLoading, error } = useQuery<ClientData, Error>({
		queryKey: ["client-data", id],
		queryFn: async () => {
			if (!id) throw new Error("Missing group id");
			return (await appService.getClientData(id)).Result;
		},
		enabled: !!id,
	});

	const documents = data?.documents || [];
	const links = data?.links || [];
	const languageSections = groupDocumentsByLanguage(documents, currentLanguage);
	const hasContent = documents.length > 0 || links.length > 0;

	const handleClick = async (item: Document | Link, isDocument: boolean) => {
		try {
			if (isDocument) {
				const doc = item as Document;
				const response = await appService.getDocumentBySasToken(doc.id);
				const sasUrl = response.Result;
				if (sasUrl) {
					window.open(sasUrl, "_blank", "noopener,noreferrer");
				} else {
					throw new Error("Invalid document URL");
				}
			} else {
				const link = item as Link;
				if (!link.url.match(/^https?:/)) throw new Error("Invalid link URL");
				window.open(link.url, "_blank", "noopener,noreferrer");
			}
		} catch (err) {
			console.error("Failed to open:", err);
			toast.error(t("calculator.documents.openError"));
		}
	};

	return (
		<div className="relative overflow-hidden bg-gradient-to-r from-moss-green-80 to-moss-green text-white pt-12 min-h-screen">
			<NavbarClient />
			<Container className="max-w-7xl mx-auto px-4 sm:px-6 mt-4 h-full flex flex-col justify-between">
				<div className="space-y-8 pb-8">
					<header className="pt-2 pb-2">
						<h1 className="text-2xl sm:text-3xl font-bold text-center sm:text-left border-b border-teal pb-3">
							{t("calculator.documents.header")}
						</h1>
					</header>

					{isLoading && (
						<div className="flex justify-center py-12">
							<Loader2 className="h-6 w-6 animate-spin text-white" />
							<span className="ml-2 text-sm">{t("calculator.documents.loading")}</span>
						</div>
					)}

					{error && (
						<p className="text-center py-12 text-red-400 text-sm">
							{t("calculator.documents.error")}
						</p>
					)}

					{!isLoading && !error && !hasContent && (
						<p className="text-center py-12 text-sm text-gray-200">
							{t("calculator.documents.empty")}
						</p>
					)}

					{!isLoading && !error && hasContent && (
						<div className="space-y-8">
							{links.length > 0 && (
								<section
									aria-labelledby="documents-links-heading"
									className="rounded-xl bg-white/10 p-4 sm:p-6 shadow-md"
								>
									<h2
										id="documents-links-heading"
										className="text-xl sm:text-2xl font-bold mb-4 border-b border-teal pb-2 text-center sm:text-left"
									>
										{t("calculator.documents.categories.links")}
									</h2>
									<ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 list-none p-0 m-0">
										{links.map((link) => (
											<li key={link.id}>
												<ItemCard
													label={link.name}
													isDocument={false}
													onOpen={() => handleClick(link, false)}
												/>
											</li>
										))}
									</ul>
								</section>
							)}

							{languageSections.map((section) => (
								<LanguageSection
									key={section.key}
									section={section}
									title={t(documentLanguageSectionTitleKey(section.key))}
									onOpenDocument={(doc) => handleClick(doc, true)}
									categoryLabel={(category) =>
										t(`calculator.documents.categories.${category}`)
									}
								/>
							))}
						</div>
					)}
				</div>
			</Container>
			<Footer />
		</div>
	);
};

type LanguageSectionProps = {
	section: DocumentLanguageSection;
	title: string;
	onOpenDocument: (doc: Document) => void;
	categoryLabel: (category: string) => string;
};

const LanguageSection = ({
	section,
	title,
	onOpenDocument,
	categoryLabel,
}: LanguageSectionProps) => {
	const headingId = `documents-lang-${section.key}`;

	return (
		<section
			aria-labelledby={headingId}
			className="rounded-xl bg-white/10 p-4 sm:p-6 shadow-md"
		>
			<h2
				id={headingId}
				className="flex items-center justify-center sm:justify-start gap-2.5 text-xl sm:text-2xl font-bold mb-5 border-b border-teal pb-2"
			>
				{section.language ? (
					<LanguageFlag language={section.language} className="h-4 w-6" />
				) : (
					<Globe className="h-5 w-5 text-white/60 shrink-0" aria-hidden />
				)}
				<span>{title}</span>
			</h2>

			<div className="space-y-6">
				{section.categories.map((group) => (
					<div key={group.category}>
						<h3 className="text-sm font-semibold tracking-wide text-white/70 mb-3 text-center sm:text-left">
							{categoryLabel(group.category)}
						</h3>
						<ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 list-none p-0 m-0">
							{group.documents.map((doc) => (
								<li key={doc.id}>
									<ItemCard
										label={doc.name}
										isDocument
										onOpen={() => onOpenDocument(doc)}
									/>
								</li>
							))}
						</ul>
					</div>
				))}
			</div>
		</section>
	);
};

type ItemCardProps = {
	label: string;
	isDocument: boolean;
	onOpen: () => void;
};

const ItemCard = ({ label, isDocument, onOpen }: ItemCardProps) => (
	<button
		type="button"
		onClick={onOpen}
		className="flex w-full items-center gap-2 p-3 sm:p-4 text-left font-header rounded-lg bg-white/10 shadow-md transition transform hover:-translate-y-1 hover:shadow-lg hover:bg-white/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal focus-visible:ring-offset-2 focus-visible:ring-offset-moss-green cursor-pointer group"
	>
		{isDocument ? (
			<FileIcon className="h-4 w-4 text-white shrink-0" aria-hidden />
		) : (
			<LinkIcon className="h-4 w-4 text-white shrink-0" aria-hidden />
		)}
		<span className="flex-1 truncate">{label}</span>
		<ArrowUpRight
			className="h-4 w-4 shrink-0 opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 transition"
			aria-hidden
		/>
	</button>
);

export default DocumentsPage;
